import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR, { Fetcher, useSWRConfig } from 'swr';

import EmptyAssigneeWarningDialog from './common/EmptyAssigneeDialog';
import MinimizedReportTab from './common/MinimizedReportTab';
import OverwriteWarningDialog from './common/OverwriteWarningDialog';
import DynamicReportDialog from './reportDialogs/DynamicReportDialog';
import ViewFinalReportDialog from './reportDialogs/ViewFinalReportDialog';

import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import { ReportTypeProps } from '../../../../components/customer/report/types';
import { ROLE_VALUES } from '../../../../constants/customerConstant';
import { CLOSED_REPORTS_STATUS_BOUNDARY } from '../../../../constants/reportConstants';
import useReportTypeData from '../../../../hooks/fetcher/useReportTypeData';
import { FinalReportData } from '../../../../interfaces/FinalReportData';
import { FinalReportTemplate } from '../../../../interfaces/FinalReportTemplate';
import { setRawFinalReport } from '../../../../slices/reportDataSlice';
import { RootState } from '../../../../utils/store';

export interface PrepopulateValues {
  reportType: ReportTypeProps;
  referenceID: string;
  assignee: string;
  priority: number;
  createdAt: string;
}

const CustomerFinalReport = () => {
  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );
  const REPORT_TEMPLATE_LIST_URL = '/final-report-templates-list';
  const REPORT_LIST_URL = `/final-reports-list?case_manager_reference_id=${reportDetails.referenceId}`;
  const { mutate } = useSWRConfig();
  const dispatch = useDispatch();

  const fetcher: Fetcher<FinalReportTemplate[], string> = async (url) => {
    const response = await axiosInstanceForTenantAPI().get(url);
    return response.data;
  };

  const listFetcher: Fetcher<FinalReportData[], string> = async (url) => {
    const response = await axiosInstanceForTenantAPI().get(url);
    return response.data.results;
  };

  const { data, error, isLoading } = useSWR(REPORT_TEMPLATE_LIST_URL, fetcher);
  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
  } = useSWR(REPORT_LIST_URL, listFetcher);

  const { reportTypeList } = useReportTypeData();

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [openViewReport, setOpenViewReport] = useState(false);
  const [openTab, setOpenTab] = useState(false);
  const [openOverwriteDialog, setOpenOverwriteDialog] = useState(false);
  const [openEmptyAssigneeDialog, setOpenEmptyAssigneeDialog] = useState(false);
  const [overridingTemplate, setOverridingTemplate] =
    useState<FinalReportTemplate | null>(null);
  const [selectedTemplate, setTemplate] = useState<FinalReportTemplate | null>(
    null,
  );
  const [isDeleting, setDeleting] = useState(false);
  const [existingReport, setReport] = useState<FinalReportData | null>(null);

  useEffect(() => {
    const userTypes = [
      ROLE_VALUES.TENANT_ADMIN,
      ROLE_VALUES.PARTNER_USER,
      ROLE_VALUES.PARTNER_ADMIN,
    ];
    const userType = parseInt(localStorage.getItem('user_type') ?? '');
    if (userTypes.includes(userType)) {
      setIsAdmin(true);
    }
  }, []);

  const open = Boolean(anchorEl);

  const handleOpenOverwrite = () => {
    setOpenOverwriteDialog(true);
  };

  const handleOpenAssigneeWarning = () => {
    setOpenEmptyAssigneeDialog(true);
  };

  const handleCloseAssigneeWarning = () => {
    setOpenEmptyAssigneeDialog(false);
  };

  const handleCloseOverwrite = () => {
    setOverridingTemplate(null);
    setOpenOverwriteDialog(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenTemplate = (item: FinalReportTemplate) => {
    if (item.case_manager_name) {
      if (!reportDetails.assignee) {
        handleOpenAssigneeWarning();
        return;
      }
    }
    if (openTab) {
      setOverridingTemplate(item);
      handleOpenOverwrite();
      return;
    }
    setAnchorEl(null);
    setTemplate(item);
    setOpenTemplateDialog(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenReport = () => {
    setOpenViewReport(true);
  };
  const handleCloseReport = () => {
    setOpenViewReport(false);
  };

  const handleMaximizeTab = () => {
    setOpenTab(false);
    setOpenTemplateDialog(true);
  };

  const handleMinimizeReport = () => {
    setOpenTab(true);
    setOpenTemplateDialog(false);
  };

  const handleCloseTemplateDialog = () => {
    setOpenTemplateDialog(false);
    setOpenTab(false);
    if (!existingReport) {
      setTemplate(null);
    }
  };

  const handleDeleteExistingDraft = async () => {
    if (!existingReport) {
      return;
    }
    setDeleting(true);
    handleCloseTemplateDialog();
    const url = `/final-reports/${existingReport.id!}`;
    const response = await axiosInstanceForTenantAPI().delete(url);
    if (response.status === StatusCodes.NO_CONTENT) {
      setReport(null);
      setTemplate(null);
      mutate(REPORT_LIST_URL);
    }
    setDeleting(false);
  };

  const handleOverwriteTemplate = () => {
    handleCloseTemplateDialog();
    handleDeleteExistingDraft();
    setOpenOverwriteDialog(false);
    setAnchorEl(null);
    setOpenTemplateDialog(true);
  };

  const HEX_GRAY_COLOR = '#878787';

  useEffect(() => {
    if (!selectedTemplate) {
      if (overridingTemplate) {
        setTemplate(overridingTemplate);
      }
    }
  }, [overridingTemplate, selectedTemplate]);

  useEffect(() => {
    if (!listData) return;
    const linkedReport = listData.find(
      (finalReport) => finalReport.report === reportDetails.reportId,
    );
    if (!linkedReport) return;
    setReport(linkedReport);
    dispatch(setRawFinalReport(linkedReport));
    if (linkedReport.status === 0) {
      if (!data) {
        return;
      }
      const draftTemplate = data.find(
        (template) => template.id === linkedReport.final_report_template,
      );
      if (!draftTemplate) {
        return;
      }
      setTemplate(draftTemplate);
    }
  }, [listData, reportDetails.reportId, data, dispatch]);

  if (isLoading && listLoading) {
    return (
      <Box
        width='100%'
        height='100%'
        display='flex'
        justifyContent='center'
        alignItems='center'
        flexGrow={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    !userAccessRoles.is_allowed_to_download_final_report &&
    (error || listError)
  ) {
    return (
      <Box>
        <Typography>
          <FormattedMessage defaultMessage='An error occurred. Please try again later.' />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Card elevation={0}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography
            variant='h4'
            sx={{
              fontFamily: 'Schibsted Grotesk',
              fontSize: '1.125rem',
              fontWeight: 500,
              lineHeight: '1.375rem',
              letterSpacing: '0em',
              textAlign: 'left',
              color: '#000000',
            }}
          >
            <FormattedMessage defaultMessage='Final Report' />
          </Typography>
          {existingReport?.status === 0 && (
            <Button
              variant='text'
              onClick={() => setOpenTemplateDialog(true)}
              disabled={isDeleting}
            >
              <Typography color='#2449FC' fontWeight={500}>
                <FormattedMessage defaultMessage='Edit' />
              </Typography>
            </Button>
          )}
          {existingReport?.status === 1 && (
            <Typography color='error'>
              <FormattedMessage defaultMessage='Closed' />
            </Typography>
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: '14px',
            marginBottom: '20px',
            marginTop: '4px',
            color: HEX_GRAY_COLOR,
            fontFamily: 'Schibsted Grotesk',
          }}
        >
          <FormattedMessage defaultMessage='Create a summary of your investigation, outcomes, and recommendations.' />
        </Typography>
        {existingReport ? (
          existingReport.status === 1 ? (
            <Button
              fullWidth
              variant='contained'
              color='primary'
              sx={{
                textTransform: 'none',
                backgroundColor: '#F6F8FF',
                color: '#2449FC',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: '#dae0f5',
                },
              }}
              onClick={handleOpenReport}
              disableElevation
            >
              <FormattedMessage defaultMessage='Final Report' />
            </Button>
          ) : (
            <Button
              fullWidth
              variant='contained'
              color='lightGray'
              sx={{
                textTransform: 'none',
                justifyContent: 'space-between',
              }}
              disabled={isDeleting}
              disableElevation
              onClick={() => setOpenTemplateDialog(true)}
            >
              <Typography fontSize={14}>
                <FormattedMessage defaultMessage='Final Report' />
              </Typography>
              <Typography fontSize={14}>
                <FormattedMessage defaultMessage='Draft' />
              </Typography>
            </Button>
          )
        ) : (
          <Button
            disabled={
              userAccessRoles?.is_read_only_report ||
              (reportDetails.status <= CLOSED_REPORTS_STATUS_BOUNDARY
                ? false
                : true) ||
              !(
                userAccessRoles?.is_allowed_to_generate_final_report ||
                reportDetails.isAssignee ||
                isAdmin
              )
            }
            id='basic-button'
            variant='contained'
            sx={{
              width: '121px',
              height: '38px',
              backgroundColor: '#2449FC',
              color: 'white',
              textTransform: 'none',
              padding: '14px 22px',
              gap: '10px',
            }}
            onClick={handleClick}
            disableElevation
          >
            <Typography
              fontWeight={600}
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: 14,
              }}
            >
              <FormattedMessage defaultMessage='Create' />
            </Typography>
            <KeyboardArrowDownIcon />
          </Button>
        )}
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                width: '280px',
              },
            },
          }}
        >
          {data &&
            data.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => handleOpenTemplate(item)}
                sx={{
                  padding: '16px',
                }}
              >
                <Typography
                  display='inline'
                  fontFamily='Schibsted Grotesk'
                  fontSize={14}
                  marginRight='2px'
                >
                  {item.template_name}
                </Typography>
                {item.is_default && (
                  <Typography
                    display='inline'
                    fontFamily='Schibsted Grotesk'
                    fontSize={12}
                    color={HEX_GRAY_COLOR}
                    marginLeft='2px'
                  >
                    <FormattedMessage defaultMessage={'(Default)'} />
                  </Typography>
                )}
              </MenuItem>
            ))}
        </Menu>
        {existingReport?.status === 1 && (
          <ViewFinalReportDialog
            open={openViewReport}
            onClose={handleCloseReport}
            id={existingReport.id!}
            disabledFlag={userAccessRoles.is_allowed_to_download_final_report}
          />
        )}
        {selectedTemplate && (
          <DynamicReportDialog
            open={openTemplateDialog}
            onClose={handleCloseTemplateDialog}
            onDelete={handleDeleteExistingDraft}
            onMinimize={handleMinimizeReport}
            existingTemplate={selectedTemplate}
            reportTypeList={reportTypeList}
            draftValues={existingReport}
            reportID={reportDetails.reportId}
            setExisting={setReport}
          />
        )}
        <OverwriteWarningDialog
          open={openOverwriteDialog}
          onClose={handleCloseOverwrite}
          onConfirm={handleOverwriteTemplate}
        />
        <EmptyAssigneeWarningDialog
          open={openEmptyAssigneeDialog}
          onClose={handleCloseAssigneeWarning}
        />
      </Card>
      {openTab && (
        <MinimizedReportTab
          title={selectedTemplate?.template_name}
          onClose={handleCloseTemplateDialog}
          onMaximize={handleMaximizeTab}
        />
      )}
    </>
  );
};

export default CustomerFinalReport;
